import {
  faCircleCheck,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelfQuery } from '../../../graphql/types';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useTracking from '../../../hooks/useTracking';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventSectionNames,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import Button, { ButtonVariant } from '../../reusable/Button';
import GenericError from '../../reusable/GenericError';
import { RadioGroup } from '../../reusable/Radio';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import { SettingsSection } from '../SettingsPage';

export enum ChatEnterKeyBehaviour {
  NewLine = 'NewLine',
  Send = 'Send',
}
interface GeneralSettingsFormValues {
  chatEnterKeyBehaviour: ChatEnterKeyBehaviour;
}
export const ChatSettings: React.FC = () => {
  const { setSnackbar } = useSnackbarContext();
  const { track } = useTracking();
  const { data, loading, error } = useSelfQuery();
  const [chatEnterKeyBehaviour, setChatEnterKeyBehaviour] =
    useLocalStorage<ChatEnterKeyBehaviour>(
      `${data?.self?.id}-preferences-chat-enter-behaviour`,
      ChatEnterKeyBehaviour.Send,
    );

  const methods = useForm<GeneralSettingsFormValues>({
    defaultValues: {
      chatEnterKeyBehaviour: ChatEnterKeyBehaviour.Send,
    },
  });
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  useEffect(() => {
    if (chatEnterKeyBehaviour) {
      reset({
        chatEnterKeyBehaviour,
      });
    }
  }, [chatEnterKeyBehaviour, reset]);

  const submitGeneralSettings = async (
    formValues: GeneralSettingsFormValues,
  ) => {
    setChatEnterKeyBehaviour(formValues.chatEnterKeyBehaviour);
    setSnackbar({
      message: 'Changes saved',
      icon: faCircleCheck,
      variant: SnackbarVariant.Success,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(submitGeneralSettings)}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
    >
      <SettingsSection
        title="Enter Key Behavior"
        subtitle="When writing a message, press Enter to…"
        icon={faExclamationCircle}
        iconTooltip="This setting is specific to this device."
      >
        {loading && (
          <LoadingPanel
            height={84}
            noBorder
            testId="chatEnterKeyBehaviour-loading"
          />
        )}
        {error && (
          <GenericError testId="chatEnterKeyBehaviour-error" error={error} />
        )}
        {!loading && (
          <div>
            <Controller
              name="chatEnterKeyBehaviour"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  label=""
                  name={field.name}
                  checkedOption={field.value}
                  onChange={({ value }) => {
                    track(EventNames.RadioClick, {
                      event_category: EventCategories.Settings,
                      event_label: value,
                      section_name: EventSectionNames.Chat,
                    });
                    field.onChange(value);
                  }}
                  options={[
                    {
                      label:
                        'Send the message (use Shift + Enter for new lines)',
                      value: ChatEnterKeyBehaviour.Send,
                    },
                    {
                      label: 'Start a new line (use ⌘/⊞/ctrl + Enter to send)',
                      value: ChatEnterKeyBehaviour.NewLine,
                    },
                  ]}
                />
              )}
            />
          </div>
        )}
      </SettingsSection>
      {!loading && (
        <Button
          variant={ButtonVariant.Dark}
          testId="chatPreferences-save"
          disabled={isSubmitting}
          type="submit"
          trackingData={{
            event_category: EventCategories.Settings,
            event_label: EventLabels.Save,
            section_name: EventSectionNames.Chat,
          }}
        >
          Save Changes
        </Button>
      )}
    </form>
  );
};
